import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Play, Pause, Square, Clock, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "@/components/common";
import { api, formatApiError } from "@/lib/api";
import { fmtDuration } from "@/lib/constants";

const localInput = (iso) => {
  if (!iso) return "";
  const parts = Object.fromEntries(new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
    .formatToParts(new Date(iso)).map((p) => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};
const hm = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Paris" }) : "—");
const minutesLabel = (m) => (m == null ? "—" : `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")}`);

function liveSeconds(state, offset) {
  if (!state) return 0;
  const a = state.active;
  if (!a || a.status !== "running") return state.worked_seconds;
  const elapsed = (Date.now() + offset - new Date(state.server_now).getTime()) / 1000;
  return state.worked_seconds + Math.max(0, elapsed);
}

const SessionDialog = ({ open, onOpenChange, session, onSubmit }) => {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  useEffect(() => {
    if (open) { setStart(localInput(session?.start)); setEnd(localInput(session?.end)); }
  }, [open, session]);
  const mins = start && end ? Math.round((new Date(end) - new Date(start)) / 60000) : null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F1F3D] border-cyan-400/20" data-testid="time-session-dialog">
        <DialogHeader><DialogTitle>{session ? "Corriger la session" : "Saisie manuelle des horaires"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <Field label="Début"><Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} data-testid="time-session-start-input" /></Field>
          <Field label="Fin"><Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="time-session-end-input" /></Field>
        </div>
        <p className="text-sm text-slate-300" data-testid="time-session-duration">Durée calculée : {mins != null && mins > 0 ? minutesLabel(mins) : "—"}</p>
        <Button disabled={!(mins > 0)} onClick={() => onSubmit({ start, end })} data-testid="time-session-save">Enregistrer</Button>
      </DialogContent>
    </Dialog>
  );
};

const AdjustDialog = ({ open, onOpenChange, state, onSubmit }) => {
  const [ret, setRet] = useState("");
  const [bill, setBill] = useState("");
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (open && state) {
      setRet(state.retained_minutes ?? Math.round(state.worked_seconds / 60));
      setBill(state.billable_minutes ?? "");
      setReason("");
    }
  }, [open, state]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F1F3D] border-cyan-400/20" data-testid="time-adjust-dialog">
        <DialogHeader><DialogTitle>Modifier le temps</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-400">Durée chronométrée : {fmtDuration(state?.worked_seconds)}</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Durée retenue (minutes)"><Input type="number" min={0} value={ret} onChange={(e) => setRet(e.target.value)} data-testid="time-retained-input" /></Field>
          <Field label="Durée facturable (minutes)"><Input type="number" min={0} value={bill} onChange={(e) => setBill(e.target.value)} placeholder="Facultatif" data-testid="time-billable-input" /></Field>
        </div>
        <Field label="Motif (historisé)"><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="time-adjust-reason-input" /></Field>
        <Button onClick={() => onSubmit({ retained_minutes: ret === "" ? null : Number(ret), billable_minutes: bill === "" ? null : Number(bill), reason })} data-testid="time-adjust-save">Enregistrer</Button>
      </DialogContent>
    </Dialog>
  );
};

export const TimerPanel = ({ interventionId, onChanged }) => {
  const [state, setState] = useState(null);
  const [offset, setOffset] = useState(0);
  const [busy, setBusy] = useState(false);
  const [, tick] = useState(0);
  const [dialog, setDialog] = useState(null);
  const busyRef = useRef(false);

  const apply = (data) => {
    setState(data);
    setOffset(new Date(data.server_now).getTime() - Date.now());
  };
  const load = useCallback(() => api.get(`/interventions/${interventionId}/time`).then((r) => apply(r.data)).catch(() => {}), [interventionId]);
  useEffect(() => {
    load();
    const t = setInterval(() => tick((n) => n + 1), 1000);
    const poll = setInterval(load, 30000);
    const vis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", vis);
    window.addEventListener("online", load);
    return () => { clearInterval(t); clearInterval(poll); document.removeEventListener("visibilitychange", vis); window.removeEventListener("online", load); };
  }, [load]);

  const run = async (fn, okMsg) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const { data } = await fn();
      apply(data);
      if (okMsg) toast.success(okMsg);
      setDialog(null);
      onChanged?.();
    } catch (e) {
      toast.error(e.response ? formatApiError(e) : "Connexion perdue : action NON enregistrée, réessayez.");
      load();
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };
  const act = (a, msg) => run(() => api.post(`/interventions/${interventionId}/time/${a === "start" ? "start" : `action/${a}`}`), msg);
  const active = state?.active;
  const secs = liveSeconds(state, offset);

  return (
    <section className="rounded-xl border border-cyan-400/25 bg-[#122549]/70 p-4 sm:p-6" data-testid="timer-panel">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-display text-base font-semibold text-white md:text-lg"><Clock className="h-5 w-5 text-cyan-300" />Chronométrage</h2>
        <span className="text-xs text-slate-400" data-testid="timer-state">{active ? (active.status === "paused" ? "En pause" : "En cours") : "Arrêté"}</span>
      </div>
      <p className={`mt-3 text-center font-mono-dt text-3xl font-semibold sm:text-5xl ${active?.status === "running" ? "text-cyan-300" : "text-white"}`} data-testid="timer-display">{fmtDuration(secs)}</p>
      <p className="mt-1 text-center text-xs text-slate-400">Début réel : {hm(state?.actual_start)} · Fin réelle : {hm(state?.actual_end)}</p>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {!active && (
          <Button disabled={busy || !state} onClick={() => act("start", "Intervention démarrée")} className="h-14 text-base font-semibold dt-glow sm:col-span-2" data-testid="timer-start-button">
            <Play className="mr-2 h-5 w-5" />DÉMARRER L'INTERVENTION
          </Button>
        )}
        {active?.status === "running" && (
          <Button disabled={busy} variant="outline" onClick={() => act("pause", "Pause")} className="h-14 text-base" data-testid="timer-pause-button"><Pause className="mr-2 h-5 w-5" />PAUSE</Button>
        )}
        {active?.status === "paused" && (
          <Button disabled={busy} onClick={() => act("resume", "Reprise")} className="h-14 text-base" data-testid="timer-resume-button"><Play className="mr-2 h-5 w-5" />REPRENDRE</Button>
        )}
        {active && (
          <Button disabled={busy} variant="destructive" onClick={() => act("stop", "Intervention terminée : complétez le compte rendu")} className="h-14 text-base" data-testid="timer-stop-button">
            <Square className="mr-2 h-5 w-5" />TERMINER L'INTERVENTION
          </Button>
        )}
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
        <div className="rounded-lg bg-white/5 p-2"><p className="text-slate-400">Chronométrée</p><p className="font-mono-dt text-white" data-testid="timer-worked">{minutesLabel(Math.round((state?.worked_seconds || 0) / 60))}</p></div>
        <div className="rounded-lg bg-white/5 p-2"><p className="text-slate-400">Retenue</p><p className="font-mono-dt text-white" data-testid="timer-retained">{minutesLabel(state?.retained_minutes ?? Math.round((state?.worked_seconds || 0) / 60))}</p></div>
        <div className="rounded-lg bg-white/5 p-2"><p className="text-slate-400">Facturable</p><p className="font-mono-dt text-white" data-testid="timer-billable">{minutesLabel(state?.billable_minutes)}</p></div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => setDialog({ kind: "adjust" })} data-testid="timer-adjust-button">Modifier le temps</Button>
        <Button variant="outline" size="sm" onClick={() => setDialog({ kind: "session" })} data-testid="timer-manual-button"><Plus className="mr-1 h-4 w-4" />Saisie manuelle</Button>
      </div>
      {state?.sessions?.length > 0 && (
        <ul className="mt-4 space-y-1 text-sm" data-testid="timer-sessions">
          {state.sessions.map((s, i) => (
            <li key={s.id} className="flex items-center justify-between gap-2 rounded-md bg-white/[0.03] px-3 py-2" data-testid={`timer-session-${i}`}>
              <span className="text-slate-300">{hm(s.start)} → {s.end ? hm(s.end) : "en cours"} {s.manual && <em className="text-xs text-slate-500">(manuelle)</em>}<span className="block text-xs text-slate-500">{s.user_name}{s.pauses.length ? ` · ${s.pauses.length} pause(s)` : ""}</span></span>
              <span className="flex items-center gap-2 font-mono-dt text-cyan-200">{minutesLabel(Math.round(s.seconds / 60))}
                {s.status === "done" && <button className="text-xs text-slate-400 underline" onClick={() => setDialog({ kind: "session", session: s })} data-testid={`timer-session-edit-${i}`}>corriger</button>}
              </span>
            </li>
          ))}
        </ul>
      )}
      <SessionDialog open={dialog?.kind === "session"} onOpenChange={(o) => !o && setDialog(null)} session={dialog?.session}
        onSubmit={(b) => run(() => (dialog.session ? api.put(`/interventions/${interventionId}/time/sessions/${dialog.session.id}`, b) : api.post(`/interventions/${interventionId}/time/sessions`, b)), "Session enregistrée")} />
      <AdjustDialog open={dialog?.kind === "adjust"} onOpenChange={(o) => !o && setDialog(null)} state={state}
        onSubmit={(b) => run(() => api.put(`/interventions/${interventionId}/time/adjust`, b), "Temps modifié")} />
    </section>
  );
};
